/** Finite impact mist made from overlapping, softly transparent faceted lobes.
 * One draw per fall; all drift and renewal run on the shared wrapped GPU clock. */
import * as THREE from 'three';
import {fallSheetPoint,rngFromString,type PlanetSampler,type RiverFall,type TerrainSample} from '@hyperlight/shared';

export const WATERFALL_MIST_LOBES=12;
const GROWTH_START=.70,GROWTH_END=1.25,BILLOW_M=.3;

/** The caller can supply its connected receiving-water span. Every emitter
 * still checks final support so roads, pads and dry shelves cannot emit mist. */
export function buildWaterfallMist(sampler:PlanetSampler,fall:RiverFall,R:number,A:number,
  water:THREE.ShaderMaterial,span:readonly[number,number]=[-fall.half,fall.half]):THREE.Mesh|null {
 const impact=fallSheetPoint(fall,1,A),up=new THREE.Vector3(impact.x,impact.y,impact.z);
 const flow=new THREE.Vector3(fall.end.x-fall.lip.x,fall.end.y-fall.lip.y,fall.end.z-fall.lip.z);
 flow.addScaledVector(up,-flow.dot(up)).normalize();
 const side=new THREE.Vector3().crossVectors(flow,up).normalize();
 const center=up.clone().multiplyScalar(R+impact.ws*A+.12),sample={}as TerrainSample;
 const rand=rngFromString(`${sampler.def.seed}:waterfall-mist:${fall.id}`);
 const energy=Math.min(1.6,Math.max(.5,Math.sqrt(fall.dropM/24)));
 const plumeHeight=Math.min(6,Math.max(1.1,fall.dropM*.13));
 // The receiving pool may be many times wider than the falling discharge.
 // Mist starts beneath that discharge, not across the whole wet pool apron.
 const low=Math.max(span[0],-fall.half*1.1),high=Math.min(span[1],fall.half*1.1);
 if(high-low<.25)return null;
 const width=high-low,middle=(low+high)*.5;
 const strength=.45+.55*Math.min(1,Math.max(0,(fall.dropM-6)/14));
 const shortScale=Math.min(1,plumeHeight/2.6);
 const source=new THREE.IcosahedronGeometry(1,0),unit=source.getAttribute('position');
 const positions:number[]=[],normals:number[]=[],origins:number[]=[],drifts:number[]=[],phases:number[]=[],rates:number[]=[],tones:number[]=[],opacities:number[]=[];
 const anchors:number[][]=[],clockCycles:number[]=[];
 const point=new THREE.Vector3(),normal=new THREE.Vector3(),edgeA=new THREE.Vector3(),edgeB=new THREE.Vector3();
 let bound=0,lobes=0;
 for(let i=0;i<WATERFALL_MIST_LOBES;i++){
  const layer=Math.floor(i/6),across=middle+((i%6+.5)/6-.5)*width*.78+(rand()-.5)*width*.055;
  const downstream=(.65+layer*1.25+rand()*.45)*shortScale;
  const anchor=center.clone().addScaledVector(side,across).addScaledVector(flow,downstream),d=anchor.clone().normalize();
  sampler.sample(d.x,d.y,d.z,sample,false);
  const ws=sample.riverWs??-1e9;
  if(sample.town>.25||sample.road<0||ws<=sample.h||Math.abs(ws-impact.ws)*A>.1)continue;
  const origin=anchor.sub(center).addScaledVector(up,plumeHeight*(.14+layer*.12+rand()*.04));
  const drift=flow.clone().multiplyScalar((.8+plumeHeight*.15)*(1+rand()*.3)*Math.sqrt(shortScale))
   .addScaledVector(side,(rand()-.5)*.55*energy).addScaledVector(up,plumeHeight*(.24+layer*.16+rand()*.08));
  const scale=new THREE.Vector3(Math.min(1.9,plumeHeight*.52,Math.max(.4,width*.14)*(1+plumeHeight*.05))*(.85+rand()*.3),
   Math.min(.75+plumeHeight*.10+rand()*.3,plumeHeight*.58),plumeHeight*(.23+layer*.065+rand()*.025));
  // Interleave the ages across the curtain, with opposing phases between
  // layers. A frozen clock still has a supported billow at every part of the
  // impact, rather than one half of the row fading out together.
  const phase=(((i%6)*5%6+.5)/6+layer*.5+rand()*.01)%1,cycles=90+(i%3)*10,tone=.94+rand()*.09,shape=rand()*6.28318530718;
  const first=positions.length/3;
  for(let j=0;j<unit.count;j++){
   const x=unit.getX(j),y=unit.getY(j),z=unit.getZ(j);
   // Identical source vertices get identical distortion, retaining a closed
   // volume with irregular facets instead of twelve identical spheres.
   const contour=1+.09*Math.sin(x*4.1+y*7.3+z*6.9+shape);
   point.copy(side).multiplyScalar(x*scale.x*contour).addScaledVector(flow,y*scale.y*contour).addScaledVector(up,z*scale.z*contour);
   positions.push(point.x,point.y,point.z);origins.push(origin.x,origin.y,origin.z);drifts.push(drift.x,drift.y,drift.z);
   phases.push(phase);rates.push(cycles/600);tones.push(tone);opacities.push((layer===0?.72:.53)*strength);
   bound=Math.max(bound,origin.length()+drift.length()+point.length()*GROWTH_END+BILLOW_M);
  }
  for(let j=first;j<positions.length/3;j+=3){
   point.fromArray(positions,j*3);edgeA.fromArray(positions,(j+1)*3).sub(point);edgeB.fromArray(positions,(j+2)*3).sub(point);
   normal.crossVectors(edgeA,edgeB).normalize();for(let k=0;k<3;k++)normals.push(normal.x,normal.y,normal.z);
  }
  anchors.push(d.toArray());clockCycles.push(cycles);lobes++;
 }
 source.dispose();if(!lobes)return null;
 const geometry=new THREE.BufferGeometry();
 for(const [name,values,size]of[
  ['position',positions,3],['normal',normals,3],['aOrigin',origins,3],['aDrift',drifts,3],
  ['aPhase',phases,1],['aRate',rates,1],['aTone',tones,1],['aOpacity',opacities,1],
 ]as const)geometry.setAttribute(name,new THREE.Float32BufferAttribute(values,size));
 geometry.boundingSphere=new THREE.Sphere(new THREE.Vector3(),bound+.01);
 const material=new THREE.ShaderMaterial({transparent:true,depthTest:true,depthWrite:false,fog:true,side:THREE.FrontSide,
  uniforms:{...THREE.UniformsUtils.clone(THREE.UniformsLib.fog),uTime:water.uniforms.uTime,uDayLight:water.uniforms.uDayLight,
   uFoam:water.uniforms.uFoam,uSunColor:water.uniforms.uSunColor,uUp:{value:up},
   uFadeNear:water.uniforms.uFadeNear,uFadeFar:water.uniforms.uFadeFar},
  vertexShader:/* glsl */`
   #include <common>
   #include <logdepthbuf_pars_vertex>
   #include <fog_pars_vertex>
   attribute vec3 aOrigin,aDrift;
   attribute float aPhase,aRate,aTone,aOpacity;
   uniform float uTime,uFadeNear,uFadeFar;
   uniform vec3 uUp;
   varying vec3 vNormal,vView;
   varying float vAlpha,vShade;
   void main(){
    float age=fract(uTime*aRate+aPhase);
    float envelope=smoothstep(0.0,.13,age)*(1.0-smoothstep(.58,1.0,age));
    vec3 p=aOrigin+aDrift*age+position*mix(${GROWTH_START},${GROWTH_END},age)
     +uUp*(sin(age*3.14159265359)*${BILLOW_M});
    vec4 mvPosition=modelViewMatrix*vec4(p,1.0);
    vNormal=normalize(normalMatrix*normal);vView=-mvPosition.xyz;
    vShade=aTone*(.88+.12*dot(normal,uUp));
    vAlpha=envelope*aOpacity*(1.0-smoothstep(min(uFadeNear,100.0),min(uFadeFar,360.0),length(mvPosition.xyz)));
    gl_Position=projectionMatrix*mvPosition;
    #include <fog_vertex>
    #include <logdepthbuf_vertex>
   }`,
  fragmentShader:/* glsl */`
   #include <common>
   #include <logdepthbuf_pars_fragment>
   #include <fog_pars_fragment>
   uniform float uDayLight;
   uniform vec3 uFoam,uSunColor;
   varying vec3 vNormal,vView;
   varying float vAlpha,vShade;
   void main(){
    #include <logdepthbuf_fragment>
    float daylight=clamp(uDayLight,0.0,1.0);
    float facing=max(0.0,dot(normalize(vNormal),normalize(vView)));
    float alpha=vAlpha*mix(.28,1.0,pow(facing,.45))*(.35+.65*daylight);
    if(alpha<.004)discard;
    // Ordinary alpha blending and nearly dark pigment at night: no additive
    // bloom, self-lit fog or bright white cloud behind a dark landscape.
    vec3 tint=.78+.22*clamp(uSunColor,vec3(0.0),vec3(1.3));
    vec3 color=min(vec3(1.0),uFoam*1.18)*tint*(.015+.98*daylight)*vShade;
    gl_FragColor=vec4(color,alpha);
    #include <fog_fragment>
   }`});
 const mesh=new THREE.Mesh(geometry,material);mesh.position.copy(center);mesh.name=`waterfall-mist:${fall.id}`;mesh.renderOrder=4;
 mesh.userData.waterfallMist={lobes,anchors,clockCycles,plumeHeightM:plumeHeight,emitterSpan:[low,high],strength,boundM:bound+.01};
 return mesh;
}
